import { apiMiddleware } from './apiMiddleware'

// TypeScript interfaces for the Attorney model
export interface Attorney {
  id: string  // Changed from number to string for UUID
  first_name: string
  last_name: string
  name: string
  email: string
  phone: string
  voicemail_ending?: string | null
  is_active?: boolean
  available?: boolean
  can_send_sms?: boolean
  created_at?: string
  updated_at?: string
}

export interface CreateAttorneyData {
  first_name: string
  last_name: string
  email: string
  phone?: string
  voicemail_ending?: string | null
  is_active?: boolean
  available?: boolean
  can_send_sms?: boolean
}

export interface AttorneysResponse {
  attorneys: Attorney[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface AttorneyDetailResponse {
  attorney: Attorney
}

export interface ApiError {
  error: boolean
  errors?: any
  message?: string
}

export interface ApiSuccess {
  error: boolean
  message: string
}

export interface PhoneAvailabilityResponse {
  available: boolean
  message: string
}

export interface EmailAvailabilityResponse {
  available: boolean
  message: string
}

export interface FilterableAttorney {
  id: string
  first_name: string
  last_name: string
  name: string
  email: string
  filter_value: string
}

export interface FilterableAttorneysResponse {
  error: boolean
  attorneys: FilterableAttorney[]
  message?: string
}

class AttorneysApiService {
  // No need for baseUrl or makeRequest - using middleware

  // Get all attorneys with search and pagination
  async getAttorneys(page: number = 1, pageSize: number = 50, search: string = ''): Promise<AttorneysResponse> {
    const params = new URLSearchParams()
    if (page > 1) params.append('page', page.toString())
    if (pageSize !== 50) params.append('page_size', pageSize.toString())
    if (search) params.append('search', search)
    
    const queryString = params.toString()
    const endpoint = queryString ? `attorneys/?${queryString}` : 'attorneys/'
    
    return apiMiddleware.get<AttorneysResponse>(endpoint)
  }

  // Get a specific attorney
  async getAttorney(id: string): Promise<AttorneyDetailResponse> {
    return apiMiddleware.get<AttorneyDetailResponse>(`attorneys/${id}/`)
  }

  // Create a new attorney
  async createAttorney(data: CreateAttorneyData): Promise<ApiSuccess> {
    return apiMiddleware.post<ApiSuccess>('attorneys/', data)
  }

  // Update an attorney
  async updateAttorney(id: string, data: Partial<CreateAttorneyData>): Promise<ApiSuccess & { attorney: Attorney }> {
    return apiMiddleware.put<ApiSuccess & { attorney: Attorney }>(`attorneys/${id}/`, data)
  }

  // Delete an attorney
  async deleteAttorney(id: string): Promise<ApiSuccess> {
    return apiMiddleware.delete<ApiSuccess>(`attorneys/${id}/`)
  }

  // Check phone availability
  async checkAttorneyPhoneAvailability(phone: string, excludeAttorneyId?: string): Promise<PhoneAvailabilityResponse> {
    const data: any = { phone }
    if (excludeAttorneyId) {
      data.exclude_attorney_id = excludeAttorneyId
    }
    
    return apiMiddleware.post<PhoneAvailabilityResponse>('attorneys/check-phone/', data)
  }

  // Check email availability
  async checkAttorneyEmailAvailability(email: string, excludeAttorneyId?: string): Promise<EmailAvailabilityResponse> {
    const data: any = { email }
    if (excludeAttorneyId) {
      data.exclude_attorney_id = excludeAttorneyId
    }
    
    return apiMiddleware.post<EmailAvailabilityResponse>('attorneys/check-email/', data)
  }

  // Get filterable attorneys (attorneys configured for filtering in leads dropdown)
  async getFilterableAttorneys(): Promise<FilterableAttorneysResponse> {
    return apiMiddleware.get<FilterableAttorneysResponse>('filterable-attorneys/')
  }
}

export const attorneysApi = new AttorneysApiService() 